"use client";

import {
    createContext,
    useContext,
    useState,
    useCallback,
    type ComponentPropsWithoutRef,
    type Ref,
} from "react";
import { cn } from "~/lib/utils";

interface TabsContextValue {
    value: string;
    onValueChange: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | undefined>(
    undefined
);

const useTabsContext = () => {
    const context = useContext(TabsContext);
    if (!context) {
        throw new Error("Tabs components must be used within a Tabs provider");
    }
    return context;
};

// ---------------- Tabs ----------------

interface TabsProps extends ComponentPropsWithoutRef<"div"> {
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    ref?: Ref<HTMLDivElement>;
}

function Tabs({
    value,
    defaultValue,
    onValueChange,
    children,
    className,
    ref,
    ...props
}: TabsProps) {
    const [internalValue, setInternalValue] = useState(
        defaultValue ?? ""
    );

    const currentValue = value ?? internalValue;

    const handleValueChange = useCallback(
        (newValue: string) => {
            if (value === undefined) {
                setInternalValue(newValue);
            }
            onValueChange?.(newValue);
        },
        [value, onValueChange]
    );

    return (
        <TabsContext.Provider
            value={{
                value: currentValue,
                onValueChange: handleValueChange,
            }}
        >
            <div ref={ref} className={cn("w-full", className)} {...props}>
                {children}
            </div>
        </TabsContext.Provider>
    );
}

// ---------------- TabsList ----------------

interface TabsListProps extends ComponentPropsWithoutRef<"div"> {
    ref?: Ref<HTMLDivElement>;
}

function TabsList({ children, className, ref, ...props }: TabsListProps) {
    return (
        <div
            ref={ref}
            role="tablist"
            className={cn(
                "flex gap-4 md:gap-10 w-full border-b border-b-[0.5px] border-gray200 items-end",
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}

// ---------------- TabsTrigger ----------------

interface TabsTriggerProps extends ComponentPropsWithoutRef<"button"> {
    value: string;
    ref?: Ref<HTMLButtonElement>;
    onTabChange?: (value: string) => void;
}

function TabsTrigger({
    value,
    children,
    className,
    disabled,
    ref,
    onTabChange,
    ...props
}: TabsTriggerProps) {
    const { value: selectedValue, onValueChange } = useTabsContext();
    const isSelected = selectedValue === value;

    const handleChangeTab = () => {
        onValueChange(value)
        onTabChange?.(value)
    }

    return (
        <button
            ref={ref}
            role="tab"
            aria-selected={isSelected}
            aria-controls={`tabpanel-${value}`}
            tabIndex={isSelected ? 0 : -1}
            disabled={disabled}
            className={cn(
                "relative font-suisseBP font-medium text-[14px] leading-[1.33] tracking-[0.08em] transition-colors border-b py-2",
                "hover:text-primary500",
                "disabled:pointer-events-none disabled:opacity-50",
                isSelected
                    ? "text-primary500 border-primary300 border-b-2"
                    : "text-primary400 border-transparent border-b-2",
                className
            )}
            onClick={handleChangeTab}
            {...props}
        >
            {children}
        </button>
    );
}

// ---------------- TabsContent ----------------

interface TabsContentProps extends ComponentPropsWithoutRef<"div"> {
    value: string;
    forceMount?: boolean;
    ref?: Ref<HTMLDivElement>;
}

function TabsContent({
    value,
    children,
    className,
    forceMount,
    ref,
    ...props
}: TabsContentProps) {
    const { value: selectedValue } = useTabsContext();
    const isSelected = selectedValue === value;

    if (!forceMount && !isSelected) {
        return null;
    }

    return (
        <div
            ref={ref}
            role="tabpanel"
            id={`tabpanel-${value}`}
            aria-labelledby={`tab-${value}`}
            tabIndex={0}
            className={cn(
                "mt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                !isSelected && "hidden",
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
